/**
 * Small squared chip for a resource channel's state, themed like EhrSyncStatusChip.
 */
import { Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import type { EhrResourceState } from '@models/ehr-sync';

import { resourceStateMessages } from '../messages';
import type { EhrSyncTone } from './tone';
import { toneTextColor, toneTintColor } from './tone';

/** Props for {@link ResourceStateChip}. */
interface ResourceStateChipProps {
  state: EhrResourceState;
}

const STATE_PALETTE: Record<EhrResourceState, EhrSyncTone> = {
  running: 'success',
  stopping: 'warning',
  error: 'error',
  paused: 'grey',
  stopped: 'grey',
};

/**
 * Squared chip with the localized channel state, tinted by state tone.
 *
 * @param props - see {@link ResourceStateChipProps}
 */
export default function ResourceStateChip({ state }: ResourceStateChipProps) {
  const { formatMessage } = useIntl();
  const palette = STATE_PALETTE[state];

  return (
    <Typography
      component="span"
      variant="caption"
      fontWeight="bold"
      sx={(theme) => {
        const color = toneTextColor(theme, palette);

        return {
          display: 'inline-flex',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color,
          backgroundColor: alpha(toneTintColor(theme, palette), 0.14),
          borderRadius: 0.75,
          px: 1,
          py: 0.375,
        };
      }}
    >
      {formatMessage(resourceStateMessages[state])}
    </Typography>
  );
}
